import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [pendingPlan, setPendingPlan] = useState('');
  const [pendingRole, setPendingRole] = useState('');
  const [changingPlanUser, setChangingPlanUser] = useState(null);
  const [newPlan, setNewPlan] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    plan: 'free',
    role: 'user',
    password: ''
  });
  const [createdUser, setCreatedUser] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit });
      if (search) params.append('search', search);
      const response = await axios.get(`/api/admin/users?${params}`);
      const data = response.data;
      if (data.pagination) {
        setUsers(data.users);
        setTotal(data.pagination.total);
        setTotalPages(data.pagination.totalPages);
      } else {
        // Backwards compat: old API returns array
        setUsers(Array.isArray(data) ? data : []);
        setTotal(Array.isArray(data) ? data.length : 0);
        setTotalPages(1);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleStartEdit = (user) => {
    setEditingUser(user.id);
    setPendingPlan(user.plan);
    setPendingRole(user.role);
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setPendingPlan('');
    setPendingRole('');
  };

  const handleSaveEdit = async (userId) => {
    try {
      setError('');
      setSuccess('');
      await axios.put(`/api/admin/users/${userId}`, {
        plan: pendingPlan,
        role: pendingRole
      });
      setSuccess('User updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchUsers();
      setEditingUser(null);
      setPendingPlan('');
      setPendingRole('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      setError('');
      setSuccess('');
      await axios.put(`/api/admin/users/${userId}`, updates);
      setSuccess('User updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchUsers();
      setEditingUser(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleSetPassword = async (userId, userName) => {
    const password = window.prompt(`Set new password for ${userName}:\n\n(Minimum 8 characters)`);
    if (!password) return;
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      setTimeout(() => setError(''), 3000);
      return;
    }
    try {
      setError('');
      setSuccess('');
      await axios.post(`/api/admin/users/${userId}/reset-password`, { password });
      setSuccess(`Password updated for ${userName}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set password');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeactivateUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to deactivate ${userName}?`)) return;
    try {
      setError('');
      setSuccess('');
      await axios.delete(`/api/admin/users/${userId}`);
      setSuccess(`User ${userName} deactivated`);
      setTimeout(() => setSuccess(''), 3000);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deactivate user');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE ${userName}?\n\nThis will delete:\n- The user account\n- All SFMC accounts\n- All monitoring configurations\n\nThis action CANNOT be undone!`)) return;
    if (!window.confirm(`FINAL WARNING: This will permanently delete ${userName} and ALL their data. Type 'DELETE' in the next prompt to confirm.`)) return;
    const confirmation = window.prompt(`Type DELETE to permanently remove ${userName}:`);
    if (confirmation !== 'DELETE') {
      setError('Deletion cancelled - confirmation text did not match');
      setTimeout(() => setError(''), 3000);
      return;
    }
    try {
      setError('');
      setSuccess('');
      await axios.delete(`/api/admin/users/${userId}/permanent`);
      setSuccess(`User ${userName} permanently deleted`);
      setTimeout(() => setSuccess(''), 3000);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleCloneUser = async (userId, userName) => {
    const email = window.prompt(`Clone ${userName} to new user.\n\nEnter email address for the new user:`);
    if (!email) return;
    const firstName = window.prompt(`Enter first name for new user (or leave blank to copy from ${userName}):`);
    const lastName = window.prompt(`Enter last name for new user (or leave blank to copy from ${userName}):`);
    try {
      setError('');
      setSuccess('');
      const response = await axios.post(`/api/admin/users/${userId}/clone`, {
        email,
        firstName: firstName || undefined,
        lastName: lastName || undefined
      });
      const { user, tempPassword, clonedAccounts, clonedConfigs } = response.data;
      alert(`User cloned successfully!\n\nNew User: ${user.email}\nTemporary Password: ${tempPassword}\n\nCloned:\n- ${clonedAccounts} SFMC Account(s)\n- ${clonedConfigs} Monitoring Config(s)\n\nPlease save this password - it will not be shown again!`);
      setSuccess(`User ${userName} cloned to ${user.email}`);
      setTimeout(() => setSuccess(''), 5000);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clone user');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleChangePlan = (user) => {
    setChangingPlanUser(user);
    setNewPlan(user.plan);
  };

  const handleSavePlanChange = async () => {
    if (!changingPlanUser) return;
    try {
      await axios.put(`/api/admin/users/${changingPlanUser.id}`, { plan: newPlan });
      setSuccess(`Plan changed to ${newPlan} for ${changingPlanUser.email}`);
      setChangingPlanUser(null);
      setNewPlan('');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change plan');
      setChangingPlanUser(null);
    }
  };

  const handleCreateUser = async () => {
    try {
      setError('');
      if (!createForm.email || !createForm.firstName || !createForm.lastName) {
        setError('Email, first name, and last name are required');
        return;
      }
      const response = await axios.post('/api/admin/users', createForm);
      const { user, tempPassword } = response.data;
      setCreatedUser({ ...user, tempPassword });
      setCreateForm({ email: '', firstName: '', lastName: '', company: '', plan: 'free', role: 'user', password: '' });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreatedUser(null);
    setCreateForm({ email: '', firstName: '', lastName: '', company: '', plan: 'free', role: 'user', password: '' });
  };

  const handleAction = (action, user) => {
    switch (action) {
      case 'changePlan': handleChangePlan(user); break;
      case 'edit': handleStartEdit(user); break;
      case 'password': handleSetPassword(user.id, user.email); break;
      case 'deactivate': handleDeactivateUser(user.id, user.email); break;
      case 'activate': handleUpdateUser(user.id, { is_active: true }); break;
      case 'clone': handleCloneUser(user.id, user.email); break;
      case 'delete': handleDeleteUser(user.id, user.email); break;
      default: break;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const paginationStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 0',
    borderTop: '1px solid var(--border-color, #e5e7eb)',
    marginTop: '8px'
  };

  const pageButtonStyle = (active) => ({
    padding: '6px 12px',
    border: '1px solid ' + (active ? '#2563eb' : '#d1d5db'),
    borderRadius: '6px',
    background: active ? '#2563eb' : 'transparent',
    color: active ? '#fff' : 'inherit',
    cursor: active ? 'default' : 'pointer',
    fontSize: '13px',
    fontWeight: active ? '600' : '400'
  });

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 7;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }

    return (
      <div style={paginationStyle}>
        <span style={{ fontSize: '13px', color: '#6b7280' }}>
          Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total} users
        </span>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <button
            style={{ ...pageButtonStyle(false), opacity: page === 1 ? 0.4 : 1 }}
            onClick={() => page > 1 && setPage(page - 1)}
            disabled={page === 1}
          >
            Prev
          </button>
          {pages.map((p, i) =>
            p === '...' ? (
              <span key={'e' + i} style={{ padding: '0 4px', color: '#9ca3af' }}>...</span>
            ) : (
              <button key={p} style={pageButtonStyle(p === page)} onClick={() => setPage(p)}>
                {p}
              </button>
            )
          )}
          <button
            style={{ ...pageButtonStyle(false), opacity: page === totalPages ? 0.4 : 1 }}
            onClick={() => page < totalPages && setPage(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  if (loading && users.length === 0) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div>
      <div className="content-header">
        <div>
          <h1 style={{ margin: '0 0 5px 0' }}>User Management</h1>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Manage user accounts, plans, and roles
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            + Create User
          </button>
          <button className="btn btn-secondary" onClick={fetchUsers}>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', padding: '16px 20px',
          background: '#fecaca', color: '#991b1b', borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', zIndex: 9999,
          minWidth: '300px', maxWidth: '500px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', padding: '16px 20px',
          background: '#d1fae5', color: '#065f46', borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', zIndex: 9999,
          minWidth: '300px', maxWidth: '500px'
        }}>
          {success}
        </div>
      )}

      <div className="content-body">
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <h3>All Users ({total})</h3>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                placeholder="Search users..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{
                  padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px',
                  fontSize: '13px', width: '220px'
                }}
              />
              <button type="submit" className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '13px' }}>
                Search
              </button>
              {search && (
                <button type="button" onClick={handleClearSearch} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '13px' }}>
                  Clear
                </button>
              )}
            </form>
          </div>
          <div className="card-body">
            {users.length > 0 ? (
              <>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Plan</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>SFMC</th>
                      <th>Configs</th>
                      <th>Registered</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td><strong>{user.first_name} {user.last_name}</strong></td>
                        <td>{user.email}</td>
                        <td>
                          {editingUser === user.id ? (
                            <select value={pendingPlan} onChange={(e) => setPendingPlan(e.target.value)} style={{ padding: '4px', borderRadius: '4px' }}>
                              <option value="free">Free</option>
                              <option value="starter">Starter</option>
                              <option value="growth">Professional</option>
                              <option value="enterprise">Enterprise</option>
                            </select>
                          ) : (
                            <span style={{
                              padding: '2px 8px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer',
                              background: user.plan === 'enterprise' ? '#ede9fe' : user.plan === 'growth' ? '#dbeafe' : user.plan === 'starter' ? '#fef3c7' : '#e5e7eb',
                              color: user.plan === 'enterprise' ? '#5b21b6' : user.plan === 'growth' ? '#1d4ed8' : user.plan === 'starter' ? '#92400e' : '#374151'
                            }} onClick={() => handleStartEdit(user)} title="Click to edit">
                              {user.plan?.toUpperCase()}
                            </span>
                          )}
                        </td>
                        <td>
                          {editingUser === user.id ? (
                            <select value={pendingRole} onChange={(e) => setPendingRole(e.target.value)} style={{ padding: '4px', borderRadius: '4px' }}>
                              <option value="user">User</option>
                              <option value="admin">Admin</option>
                            </select>
                          ) : (
                            <span style={{
                              padding: '2px 8px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer',
                              background: user.role === 'admin' ? '#fee2e2' : '#e5e7eb',
                              color: user.role === 'admin' ? '#991b1b' : '#374151'
                            }} onClick={() => handleStartEdit(user)} title="Click to edit">
                              {user.role}
                            </span>
                          )}
                        </td>
                        <td>
                          <span style={{ color: user.is_active ? '#16a34a' : '#dc2626', fontWeight: '500' }}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>{user.sfmc_accounts_count}</td>
                        <td style={{ textAlign: 'center' }}>{user.monitoring_configs_count}</td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>
                          {editingUser === user.id ? (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button className="btn btn-primary" style={{ padding: '4px 8px', fontSize: '11px' }} onClick={() => handleSaveEdit(user.id)}>Save</button>
                              <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '11px' }} onClick={handleCancelEdit}>Cancel</button>
                            </div>
                          ) : (
                            <select
                              style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #d1d5db', cursor: 'pointer', minWidth: '100px' }}
                              value=""
                              onChange={(e) => { if (e.target.value) { handleAction(e.target.value, user); e.target.value = ''; } }}
                            >
                              <option value="">Actions...</option>
                              <option value="changePlan">Change Plan</option>
                              <option value="edit">Edit Plan/Role</option>
                              <option value="password">Set Password</option>
                              {user.is_active ? <option value="deactivate">Deactivate</option> : <option value="activate">Activate</option>}
                              <option value="clone">Clone User</option>
                              <option value="delete">Delete User</option>
                            </select>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {renderPagination()}
              </>
            ) : (
              <div className="empty-state">
                <p>{search ? 'No users match your search' : 'No users found'}</p>
              </div>
            )}
          </div>
        </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--card-bg, white)', borderRadius: '12px', padding: '30px', maxWidth: '500px', width: '90%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            {createdUser ? (
              <>
                <h3 style={{ marginBottom: '20px', fontSize: '20px', color: '#16a34a' }}>User Created Successfully!</h3>
                <div style={{ background: '#f0fdf4', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                  <p style={{ margin: '0 0 10px 0' }}><strong>Email:</strong> {createdUser.email}</p>
                  <p style={{ margin: '0 0 10px 0' }}><strong>Name:</strong> {createdUser.first_name} {createdUser.last_name}</p>
                  <p style={{ margin: '0 0 10px 0' }}><strong>Plan:</strong> {createdUser.plan}</p>
                  <p style={{ margin: '0 0 10px 0' }}><strong>Role:</strong> {createdUser.role}</p>
                  {createdUser.tempPassword && (
                    <div style={{ marginTop: '15px', padding: '15px', background: '#fef3c7', borderRadius: '6px' }}>
                      <p style={{ margin: '0 0 5px 0', fontWeight: '600', color: '#92400e' }}>Temporary Password:</p>
                      <code style={{ fontSize: '16px', fontWeight: 'bold', color: '#1f2937' }}>{createdUser.tempPassword}</code>
                      <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#92400e' }}>Please save this password - it will not be shown again!</p>
                    </div>
                  )}
                </div>
                <button onClick={handleCloseCreateModal} className="btn btn-primary" style={{ width: '100%' }}>Done</button>
              </>
            ) : (
              <>
                <h3 style={{ marginBottom: '20px', fontSize: '20px' }}>Create New User</h3>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>Email *</label>
                  <input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }} placeholder="user@example.com" />
                </div>
                <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>First Name *</label>
                    <input type="text" value={createForm.firstName} onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }} placeholder="John" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>Last Name *</label>
                    <input type="text" value={createForm.lastName} onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }} placeholder="Doe" />
                  </div>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>Company</label>
                  <input type="text" value={createForm.company} onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }} placeholder="Company Name" />
                </div>
                <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>Plan</label>
                    <select value={createForm.plan} onChange={(e) => setCreateForm({ ...createForm, plan: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}>
                      <option value="free">Free</option>
                      <option value="starter">Starter</option>
                      <option value="growth">Professional</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>Role</label>
                    <select value={createForm.role} onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}>
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>Password</label>
                  <input type="text" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }} placeholder="Leave blank to auto-generate" />
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#6b7280' }}>Leave blank to auto-generate a temporary password</p>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button onClick={handleCloseCreateModal} className="btn btn-secondary">Cancel</button>
                  <button onClick={handleCreateUser} className="btn btn-primary">Create User</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Change Plan Modal */}
      {changingPlanUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--card-bg, white)', borderRadius: '12px', padding: '30px', maxWidth: '500px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ marginBottom: '20px', fontSize: '20px' }}>Change Plan</h3>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ marginBottom: '15px', fontSize: '14px', color: '#6b7280' }}>User: <strong style={{ color: 'var(--text-primary, #1f2937)' }}>{changingPlanUser.email}</strong></p>
              <p style={{ marginBottom: '15px', fontSize: '14px', color: '#6b7280' }}>Current Plan: <strong style={{ color: 'var(--text-primary, #1f2937)', textTransform: 'capitalize' }}>{changingPlanUser.plan}</strong></p>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>New Plan</label>
              <select value={newPlan} onChange={(e) => setNewPlan(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' }}>
                <option value="free">Free - $0/month</option>
                <option value="starter">Starter - $99/month</option>
                <option value="growth">Professional - $299/month</option>
                <option value="enterprise">Enterprise - $799/month</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setChangingPlanUser(null); setNewPlan(''); }} className="btn btn-secondary">Cancel</button>
              <button onClick={handleSavePlanChange} disabled={newPlan === changingPlanUser.plan} className="btn btn-primary" style={{ opacity: newPlan === changingPlanUser.plan ? 0.5 : 1, cursor: newPlan === changingPlanUser.plan ? 'not-allowed' : 'pointer' }}>Change Plan</button>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
}

export default AdminUsers;
